import { useState } from 'react';
import { earlierVersions, latestEdit, type EarlierVersion, type EditLike } from '../earlierVersions.ts';
import { wordDiff } from '../wordDiff.ts';

/**
 * Something edited, shown as Arthur asked on 19 October 2026 (an edited visit note was on screen up to four times):
 * the screen shows the CURRENT version once; this adds the "Edited" marker (who, when, why, from 123's reasons) and
 * "See earlier versions", which expands only the versions before the current one, oldest to newest, each with its
 * date, author and reason, and a word diff of what the next version changed. Used by welfare visits (125), site
 * visits (122) and buildings (123). Contacts (124) never repeated a value: their history names the fields only.
 */

/** An instant in the viewer's own zone (a timestamptz: converting it is right). */
function when(instant: string): string {
  return new Date(instant).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

/** "Edited · Correction · Editor One, 19 Oct 2026, 11:00": the edit that made the current version. */
export function EditedMarker({ edits }: { edits: EditLike[] | undefined }) {
  const last = edits ? latestEdit(edits) : null;
  if (!last) return null;
  return (
    <p className="text-sm text-neutral-700">
      <span className="rounded bg-note-bg px-1.5 py-0.5 font-semibold text-note-fg">Edited</span>{' '}
      {last.reason ?? 'no reason given'}{last.reason_text ? `: ${last.reason_text}` : ''}
      {' · '}{last.by ?? 'someone not on record'}, {when(last.at)}
      {(last.notes ?? []).map((n) => <span key={n} className="block">{n}</span>)}
    </p>
  );
}

/** "See earlier versions (N)" and, open, the previous versions only. */
export function EarlierVersions({ edits, first = null, loading = false, error = null, format, withheldWords }: {
  edits: EditLike[] | undefined;
  first?: { by: string | null; at: string | null } | null;
  loading?: boolean;
  error?: Error | null;
  /** How a field's value reads (a date, a visit type); the value itself otherwise. */
  format?: (field: string, value: string | null) => string;
  /** What a withheld change says instead of its words. */
  withheldWords?: string;
}) {
  const [open, setOpen] = useState(false);
  const earlier = edits ? earlierVersions(edits, first) : [];
  if (!loading && !error && earlier.length === 0) return null;
  return (
    <div>
      <button type="button" aria-expanded={open} onClick={() => setOpen((o) => !o)}
              className="min-h-[44px] text-sm font-semibold text-neutral-800 underline underline-offset-2">
        {open ? 'Hide earlier versions' : `See earlier versions${earlier.length ? ` (${earlier.length})` : ''}`}
      </button>
      {open && (
        error ? <p className="text-sm text-neutral-700">The earlier versions could not be loaded.</p>
          : loading ? <p className="text-sm text-neutral-600">Loading…</p>
            : (
              <ol className="mt-1 space-y-2 border-l-2 border-neutral-300 pl-3">
                {earlier.map((e) => <Version key={e.version} v={e} format={format} withheldWords={withheldWords} />)}
              </ol>
            )
      )}
    </div>
  );
}

function Version({ v, format, withheldWords }: { v: EarlierVersion; format?: (field: string, value: string | null) => string; withheldWords?: string }) {
  const show = (field: string, value: string | null) => (format ? format(field, value) : value ?? 'blank');
  return (
    <li className="text-sm">
      <p className="font-semibold text-neutral-900">
        {v.made.kind === 'first' ? 'As first recorded' : `Edited · ${v.made.reason ?? 'no reason given'}`}
        <span className="font-normal text-neutral-600">
          {v.made.by || v.made.at ? ' · ' : ''}{v.made.by ?? (v.made.at ? 'someone not on record' : '')}{v.made.at ? `, ${when(v.made.at)}` : ''}
        </span>
      </p>
      {v.made.kind === 'edit' && v.made.reason_text && <p className="text-neutral-700">Why: {v.made.reason_text}</p>}
      {v.made.kind === 'edit' && v.made.notes.map((n) => <p key={n} className="text-neutral-700">{n}</p>)}
      <ul className="mt-1 space-y-1">
        {v.fields.map((f) => (
          <li key={f.field} className="text-neutral-800">
            <span className="font-medium">{f.label}:</span>{' '}
            {f.withheld ? (withheldWords ?? 'changed') : <Diff before={show(f.field, f.value)} after={show(f.field, f.next)} />}
          </li>
        ))}
      </ul>
    </li>
  );
}

/** This version's words, with what the next version removed struck through and what it added underlined. */
function Diff({ before, after }: { before: string; after: string }) {
  return (
    <span className="whitespace-pre-line">
      {wordDiff(before, after).map((p, i) =>
        p.kind === 'same' ? <span key={i}>{p.text}</span>
          : p.kind === 'removed'
            ? <del key={i} className="rounded-sm bg-diff-del-bg text-diff-del-fg"><span className="sr-only">removed: </span>{p.text}</del>
            : <ins key={i} className="rounded-sm bg-diff-add-bg text-diff-add-fg underline"><span className="sr-only">added next: </span>{p.text}</ins>)}
    </span>
  );
}
